import * as UUID from 'uuidjs';
import ErrorReportingService from '../services/ErrorReportingService';
import { Action, Dispatch } from 'redux';
import { ClientShortHandName } from '@snoam/mono-scc';
import { Enum } from 'typescript-string-enums';
import {
  getClientId,
  getSchibstedEnvName,
  isProduction
} from '../fastflowUtils';
import {
  IAddress,
  IDuplicateAndQuarantine,
  IOrganizationData,
  IProductInfo,
  IUserInfo,
  IUserLoggedIn,
  ProductId
} from '../CustomTypes';
import {
  Identity,
  IHasSessionFailureResponse,
  IHasSessionSuccessResponse,
  IIdentityOptions
} from '@schibsted/account-sdk-browser';
import 'whatwg-fetch';
/* tslint:disable:max-classes-per-file max-line-length */

export const windowUuid = () => {
  const w: any = window;
  w.snoamUuid = w.snoamUiud || UUID.generate();
  return w.snoamUuid;
};
const fetchWithUuid: (
  input: RequestInfo,
  init?: RequestInit
) => Promise<Response> = (input, init) => {
  const uuidRequestHeader = { 'x-snoam-request-uuid': UUID.generate() };
  const uuidWindowHeader = { 'x-snoam-window-uuid': windowUuid() };
  const initWithUuid = Object.assign({}, init ? init : {}, {
    headers: Object.assign(
      {},
      init && init.headers ? init.headers : {},
      uuidRequestHeader,
      uuidWindowHeader
    )
  });
  return fetch(input, initWithUuid);
};

export type apiStatuses =
  | 'API_CONFLICT'
  | 'API_ERROR'
  | 'API_LOADING'
  | 'API_SUCCESS'
  | 'VALIDATION_ERROR'
  | 'AGREEMENT_NOT_ACCEPTED';
export const apiStatuses: { [k: string]: apiStatuses } = {
  AGREEMENT_NOT_ACCEPTED: 'AGREEMENT_NOT_ACCEPTED' as apiStatuses,
  CONFLICT: 'API_CONFLICT' as apiStatuses,
  ERROR: 'API_ERROR' as apiStatuses,
  LOADING: 'API_LOADING' as apiStatuses,
  SUCCESS: 'API_SUCCESS' as apiStatuses,
  VALIDATION_ERROR: 'VALIDATION_ERROR' as apiStatuses
};

export class ApiError extends Error {
  public response: string;
  public status: number;
  public errorCode?: number;
  public data: string;
  public requestUuid?: string;
  public requestUrl?: string;
}

export const validationErrors = {
  PURCHASE_FAILED: 'PURCHASE_FAILED',
  USER_AGREEMENT_NOT_ACCEPTED: 'USER_AGREEMENT_NOT_ACCEPTED'
};

export interface IExceptionAction extends Action {
  type: exceptionTypes;
  exception: object;
}

export const exceptionTypes = Enum('EXCEPTION');
export type exceptionTypes = keyof typeof exceptionTypes;

export type LogException = (
  type: exceptionTypes,
  exception: object
) => (dispatch: Dispatch<IExceptionAction>) => void;
export const logException: LogException = (type, exception) => dispatch => {
  if (window && window.console) {
    /*tslint:disable:no-console*/
    window.console.log(type, exception);
  }
  dispatch({ type, exception });
};

export const actionTypes = Enum(
  'ADD_FAMILY_SHARING',
  'CHECK_CODE',
  'CHECK_DUPLICATE_AND_QUARANTINE',
  'CLEAR_SPID_TOKEN',
  'CREATE_ORDER',
  'CREATE_SPID_USER',
  'FETCH_CUSTOMERSERVICE_INFO',
  'FETCH_BRREG_INFO',
  'SET_BRREG_INFO',
  'SET_BRREG_INPUT',
  'FETCH_USER_INFO',
  'FIND_USER_BY_EMAIL',
  'FIND_USER_BY_PHONE_NUMBER',
  'HIDE_TIMEOUT',
  'SEND_EMAIL',
  'SEND_LOGIN_LINK',
  'SEND_SMS',
  'SET_ALTERNATE_OFFER_PRODUCT',
  'SET_FULL_PRICE_PRODUCT',
  'SET_PRODUCT',
  'SET_PRODUCT_QUARANTINED',
  'SET_REDIRECT_URI',
  'SET_REFERER',
  'SET_SELECTED_PAYMENT_METHOD',
  'SET_SESSION_INFO',
  'SET_SIG_AND_SPIDID_FROM_URL',
  'SET_UPSALE_PRODUCT_INFO',
  'SET_UTM_SOURCE',
  'SET_UTM_MEDIUM',
  'SET_UTM_CAMPAIGN',
  'SET_UTM_TERM',
  'SET_UTM_CONTENT',
  'SHOW_TIMEOUT',
  'USER_ENTERS_APARTMENT_NUMBER',
  'USER_ENTERS_BIRTH_DAY',
  'USER_ENTERS_BIRTH_MONTH',
  'USER_ENTERS_BIRTH_YEAR',
  'USER_ENTERS_CITY',
  'USER_ENTERS_CODE',
  'USER_ENTERS_DELIVERY_POINT_ID',
  'USER_ENTERS_EMAIL',
  'USER_ENTERS_ENTRANCE',
  'USER_ENTERS_FIRST_NAME',
  'USER_ENTERS_FLOOR',
  'USER_ENTERS_HOUSE_NUMBER',
  'USER_ENTERS_LAST_NAME',
  'USER_ENTERS_PHONE_NUMBER',
  'USER_ENTERS_STREET_ADDRESS',
  'USER_ENTERS_ZIPCODE',
  'USER_ENTERS_LOYALTY_CODE',
  'VALIDATE_LOYALTY_CODE'
);
export type actionTypes = keyof typeof actionTypes;

export class Deferred<T> {
  public reject: (reason?: any) => any;
  public resolve: (value?: T | PromiseLike<T> | Response) => any;
  public promise: Promise<T>;

  constructor() {
    this.promise = new Promise<T>((resolve, reject) => {
      this.reject = reject;
      this.resolve = resolve;
    });
  }
}

export const productInfoDeferred: Deferred<IProductInfo> = new Deferred<
  IProductInfo
>();

export type fetchUserInfoDeferred = Deferred<IUserInfo>;
export const fetchUserInfoDeferred: fetchUserInfoDeferred = new Deferred<
  IUserInfo
>();

export type userLoggedInDeferred = Deferred<IUserLoggedIn>;
export const userLoggedInDeferred: userLoggedInDeferred = new Deferred<
  IUserLoggedIn
>();

export type checkDuplicateAndQuarantineDeferred = Deferred<
  IDuplicateAndQuarantine
>;
export const checkDuplicateAndQuarantineDeferred: checkDuplicateAndQuarantineDeferred = new Deferred<
  IDuplicateAndQuarantine
>();
export type ValidateLoyaltyCodeDeferred = (
  lp: string,
  code: string,
  ai?: string
) => (dispatch: Dispatch<Action>) => Promise<Response> | any;

const checkError: (res: Response) => Response | any = res => {
  if (res.status >= 200 && res.status < 300) {
    return res;
  }
  return res.text().then(text => {
    const error = new ApiError(res.statusText);
    error.response = `${res.status}: ${text || res.statusText}`;
    error.status = res.status;
    error.data = text;
    error.requestUrl = res.url;
    error.requestUuid = res.headers.get('x-snoam-request-uuid');
    throw error;
  });
};

export type fetchApi = (
  url: string,
  actionType: actionTypes,
  successCallback?: (r: Response | any) => any,
  failedCallback?: (r: Error) => any
) => (dispatch: Dispatch<Action>) => Promise<Response> | any;
export const fetchApi: fetchApi = (
  url,
  actionType,
  successCallback,
  failedCallback
) => dispatch => {
  const doFailed = (error: any) => {
    ErrorReportingService.report(error);

    if (failedCallback) {
      failedCallback(error);
    }

    return dispatch({
      data: {
        data: error.data,
        message: error.message,
        status: error.status
      },
      status: apiStatuses.ERROR,
      type: actionType
    });
  };

  dispatch({ type: actionType, status: apiStatuses.LOADING });

  return fetchWithUuid(url)
    .then(checkError)
    .then(res => res.json())
    .then(data => {
      dispatch({ type: actionType, status: apiStatuses.SUCCESS, data });
      if (successCallback) {
        successCallback(data);
      }
      return data;
    })
    .catch(doFailed);
};

export type CheckProductQuarantined = (
  productId: ProductId,
  spidId: number
) => (dispatch: Dispatch<Action>) => Promise<Response>;
export const checkProductQuarantined: CheckProductQuarantined = (
  productId,
  spidId
) =>
  fetchApi(
    `/bestilling/api/v2/ff/product/${productId}/quarantined.json?spidId=${spidId}`,
    actionTypes.SET_PRODUCT_QUARANTINED
  );

export type FetchProductInfo = (
  productId: number | string
) => (dispatch: Dispatch<Action>) => Promise<Response> | any;
export const fetchAlternateOfferProductInfo: FetchProductInfo = productId =>
  fetchApi(
    `/bestilling/api/v2/ff/product/${productId}.json`,
    actionTypes.SET_ALTERNATE_OFFER_PRODUCT
  );
export const fetchFullPriceProductInfo: FetchProductInfo = productId =>
  fetchApi(
    `/bestilling/api/v2/ff/product/${productId}.json`,
    actionTypes.SET_FULL_PRICE_PRODUCT
  );
export const fetchUpsaleProductInfo: FetchProductInfo = productId =>
  fetchApi(
    `/bestilling/api/v2/ff/product/${productId}.json`,
    actionTypes.SET_UPSALE_PRODUCT_INFO
  );
export const fetchProductInfo: FetchProductInfo = productId => {
  return fetchApi(
    `/bestilling/api/v2/ff/product/${productId}.json`,
    actionTypes.SET_PRODUCT,
    productInfoDeferred.resolve,
    productInfoDeferred.reject
  );
};

export type fetchBrregInfo = (
  orgNumber: string
) => (dispatch: Dispatch<Action>) => any;
export const fetchBrregInfo: fetchBrregInfo = (orgnumber: string) => (
  dispatch: Dispatch<Action>
) => {
  dispatch({
    status: apiStatuses.LOADING,
    type: actionTypes.FETCH_BRREG_INFO
  });
  dispatch({
    data: {} as IOrganizationData,
    type: actionTypes.SET_BRREG_INFO
  });
  fetch(`https://data.brreg.no/enhetsregisteret/api/enheter/${orgnumber}`)
    .then(result => checkError(result))
    .then(res => res.json())
    .then(data => {
      dispatch({
        data,
        status: apiStatuses.SUCCESS,
        type: actionTypes.SET_BRREG_INFO
      });
    });
};

export type fetchCustomerServiceInfo = () => (
  dispatch: Dispatch<Action>
) => any;

export const fetchCustomerServiceInfo: fetchCustomerServiceInfo = () => (
  dispatch: Dispatch<Action>
) => {
  dispatch({
    status: apiStatuses.LOADING,
    type: actionTypes.FETCH_CUSTOMERSERVICE_INFO
  });

  fetch(`/bestilling/api/v2/business/clientinfo.json`)
    .then(res => res.json())
    .then(data => {
      dispatch({
        data,
        status: apiStatuses.SUCCESS,
        type: actionTypes.FETCH_CUSTOMERSERVICE_INFO
      });
    });
};

function isIHasSessionSuccessResponse(
  r: IHasSessionSuccessResponse | IHasSessionFailureResponse
): r is IHasSessionSuccessResponse {
  return (r as IHasSessionSuccessResponse).result;
}

export function spidOptions(
  clientShortHandName: ClientShortHandName | '',
  isProduction: boolean
): IIdentityOptions {
  const redirectUri = `https://us-central1-s-plat.cloudfunctions.net/redirect-helper-${
    isProduction ? 'prod' : 'stage'
  }`;
  const domains: { [k: string]: string } = {
    AGR: 'agder24.no',
    AP: 'aftenposten.no',
    ASK: 'av-avis.no',
    BTI: 'bt.no',
    BYN: 'bygdanytt.no',
    DNB: 'aftenposten.no',
    // DP: 'e24dinepenger.no',
    FAK: 'lister24.no',
    FVN: 'fvn.no',
    KVN: 'kvinesdal24.no',
    LIN: 'l-a.no',
    MPL: 'magasinpluss.no',
    SA: 'aftenbladet.no',
    SCH: 'schibsted.no',
    STR: 'strilen.no',
    SYS: 'sysla.no',
    VG: 'vg.no',
    VNY: 'vestnytt.no'
  };
  const domain = domains[clientShortHandName];
  const sessionDomain =
    domain &&
    `https://id${isProduction ? '' : '-pre'}.${domains[clientShortHandName]}`;
  return {
    clientId: getClientId(clientShortHandName),
    env: getSchibstedEnvName(),
    redirectUri,
    sessionDomain
  };
}

export function getIdentity(
  clientShortHandName: ClientShortHandName | ''
): Identity {
  const spidOpts: IIdentityOptions = spidOptions(
    clientShortHandName,
    isProduction()
  );
  const identity = new Identity(spidOpts);
  (identity as any)._enableSessionCaching = false;
  (window as any).identity = identity;
  return identity;
}
export function loadIdentity(): Promise<Identity> {
  return new Promise<Identity>((resolve, reject) => {
    productInfoDeferred.promise
      .then(productInfo => resolve(getIdentity(productInfo.owningClient)))
      .catch(reject);
  });
}

export type InitializeSpid = () => (dispatch: Dispatch<Action>) => Promise<any>;
export type InitializeSpidBound = () => Promise<any>;
export const initializeSpid: InitializeSpid = () => (
  dispatch: Dispatch<Action>
) => {
  const deferred = new Deferred();
  dispatch({ type: actionTypes.SET_SESSION_INFO, status: apiStatuses.LOADING });

  const failedCallback = (data: any) => {
    deferred.reject(data);
    return dispatch({
      data,
      status: apiStatuses.ERROR,
      type: actionTypes.SET_SESSION_INFO
    });
  };

  loadIdentity()
    .then(identity => identity.hasSession())
    .then(data => {
      if (isIHasSessionSuccessResponse(data)) {
        dispatch({
          data,
          status: apiStatuses.SUCCESS,
          type: actionTypes.SET_SESSION_INFO
        });
        deferred.resolve(data);
        userLoggedInDeferred.resolve({ spidId: data.userId, sig: data.sig });
      } else {
        if (data.userStatus === 'notConnected') {
          dispatch({
            data,
            status: apiStatuses.SUCCESS,
            type: actionTypes.SET_SESSION_INFO
          });
          deferred.resolve(data);
        } else {
          failedCallback(data);
        }
      }
    })
    .catch(failedCallback);

  return deferred.promise;
};

function createLink1And2AsUrlParams(link1: string, link2: string) {
  return `link1=${encodeURIComponent(link1)}&link2=${encodeURIComponent(
    link2
  )}`;
}

export type findUserByEmail = (
  email: string,
  linkHasPaymentMethod: string,
  linkNoPaymentMethod?: string
) => (dispatch: Dispatch<Action>) => Promise<any>;
export const findUserByEmail: findUserByEmail = (
  email,
  linkHasPaymentMethod,
  linkNoPaymentMethod
) => dispatch => {
  const failedCallback = (data: any) => {
    dispatch({
      data,
      status: apiStatuses.ERROR,
      type: actionTypes.FIND_USER_BY_EMAIL
    });
    return findUserByEmailDeferred.reject(data);
  };

  const findUserByEmailDeferred = new Deferred();

  dispatch({
    status: apiStatuses.LOADING,
    type: actionTypes.FIND_USER_BY_EMAIL
  });

  productInfoDeferred.promise.then(productInfo => {
    fetchWithUuid(
      `/bestilling/c/${
        productInfo.owningClient
      }/api/v2/users/emailTrigger/${encodeURIComponent(
        email
      )}.json?${createLink1And2AsUrlParams(
        linkHasPaymentMethod,
        linkNoPaymentMethod
      )}`
    )
      .then(checkError)
      .then(res => res.json())
      .then(data => {
        dispatch({
          data,
          status: apiStatuses.SUCCESS,
          type: actionTypes.FIND_USER_BY_EMAIL
        });
        return findUserByEmailDeferred.resolve(data);
      })
      .catch(failedCallback);
  }, failedCallback);

  return findUserByEmailDeferred.promise;
};

export type findUserByPhoneNumber = (
  phoneNumber: string,
  email: string,
  linkHasPaymentMethod: string,
  linkNoPaymentMethod?: string
) => (dispatch: Dispatch<Action>) => Promise<any>;
export const findUserByPhoneNumber: findUserByPhoneNumber = (
  phoneNumber,
  email,
  linkHasPaymentMethod,
  linkNoPaymentMethod
) => dispatch => {
  const failedCallback = (data: any) => {
    dispatch({
      data,
      status: apiStatuses.ERROR,
      type: actionTypes.FIND_USER_BY_PHONE_NUMBER
    });
    return findUserByPhoneNumberDeferred.reject(data);
  };

  const findUserByPhoneNumberDeferred = new Deferred();
  dispatch({
    status: apiStatuses.LOADING,
    type: actionTypes.FIND_USER_BY_PHONE_NUMBER
  });

  productInfoDeferred.promise.then(productInfo => {
    const phoneNumberUriEncoded = encodeURIComponent(phoneNumber);
    fetchWithUuid(
      `/bestilling/c/${
        productInfo.owningClient
      }/api/v2/users/phoneTrigger/${phoneNumberUriEncoded}.json?email=${encodeURIComponent(
        email
      )}&${createLink1And2AsUrlParams(
        linkHasPaymentMethod,
        linkNoPaymentMethod
      )}`
    )
      .then(checkError)
      .then(res => res.json())
      .then(data => {
        dispatch({
          data,
          status: apiStatuses.SUCCESS,
          type: actionTypes.FIND_USER_BY_PHONE_NUMBER
        });
        return findUserByPhoneNumberDeferred.resolve(data);
      })
      .catch(failedCallback);
  }, failedCallback);

  return findUserByPhoneNumberDeferred.promise;
};

export type CheckCode = (
  code: string,
  email: string
) => (dispatch: Dispatch<Action>) => Promise<any>;
export type CheckCodeBound = (code: string, email: string) => Promise<any>;
export const checkCode: CheckCode = (code: string, email: string) => (
  dispatch: Dispatch<Action>
) => {
  const checkCodeDeferred = new Deferred();
  const failedCallback = (data: any) => {
    dispatch({ type: actionTypes.CHECK_CODE, status: apiStatuses.ERROR, data });
    return checkCodeDeferred.reject(data);
  };

  dispatch({ type: actionTypes.CHECK_CODE, status: apiStatuses.LOADING });

  productInfoDeferred.promise.then(productInfo => {
    fetchWithUuid(
      `/bestilling/c/${
        productInfo.owningClient
      }/api/v2/ff/checkCode.json?email=${encodeURIComponent(
        email
      )}&code=${code}`
    )
      .then(checkError)
      .then(res => res.json())
      .then(data => {
        dispatch({
          data,
          status: apiStatuses.SUCCESS,
          type: actionTypes.CHECK_CODE
        });
        userLoggedInDeferred.resolve({ spidId: data.spidId, sig: data.sig });
        return checkCodeDeferred.resolve(data);
      })
      .catch(failedCallback);
  }, failedCallback);

  return checkCodeDeferred.promise;
};

export type SendSms = (
  email: string
) => (dispatch: Dispatch<Action>) => Promise<any>;
export const sendSms: SendSms = (email: string) => (
  dispatch: Dispatch<Action>
) => {
  const failedCallback = (data: any) => {
    dispatch({ type: actionTypes.SEND_SMS, status: apiStatuses.ERROR, data });
    return sendSmsDeferred.reject(data);
  };
  const sendSmsDeferred = new Deferred();
  dispatch({ type: actionTypes.SEND_SMS, status: apiStatuses.LOADING });

  productInfoDeferred.promise.then(productInfo => {
    fetchWithUuid(
      `/bestilling/c/${
        productInfo.owningClient
      }/api/v2/ff/sendSms.json?email=${encodeURIComponent(email)}`
    )
      .then(checkError)
      .then(res => res.json())
      .then(data => {
        dispatch({
          data,
          status: apiStatuses.SUCCESS,
          type: actionTypes.SEND_SMS
        });
        return sendSmsDeferred.resolve(data);
      })
      .catch(failedCallback);
  }, failedCallback);

  return sendSmsDeferred.promise;
};

export type SendEmail = (
  email: string
) => (dispatch: Dispatch<Action>) => Promise<any>;
export const sendEmail: SendEmail = (email: string) => (
  dispatch: Dispatch<Action>
) => {
  const failedCallback = (data: any) => {
    dispatch({ type: actionTypes.SEND_EMAIL, status: apiStatuses.ERROR, data });
    return sendEmailDeferred.reject(data);
  };
  const sendEmailDeferred = new Deferred();
  dispatch({ type: actionTypes.SEND_EMAIL, status: apiStatuses.LOADING });

  productInfoDeferred.promise.then(productInfo => {
    fetchWithUuid(
      `/bestilling/c/${
        productInfo.owningClient
      }/api/v2/ff/sendEmail.json?email=${encodeURIComponent(email)}`
    )
      .then(checkError)
      .then(res => res.json())
      .then(data => {
        dispatch({
          data,
          status: apiStatuses.SUCCESS,
          type: actionTypes.SEND_EMAIL
        });
        return sendEmailDeferred.resolve(data);
      })
      .catch(failedCallback);
  }, failedCallback);

  return sendEmailDeferred.promise;
};

export type sendLoginLink = (
  email: string,
  redirectUri: string
) => (dispatch: Dispatch<Action>) => Promise<Action>;
export const sendLoginLink: sendLoginLink = (
  email,
  redirectUri
) => dispatch => {
  const failedCallback = (data: any) => {
    dispatch({
      data,
      status: apiStatuses.ERROR,
      type: actionTypes.SEND_LOGIN_LINK
    });
    return sendLoginLinkDeferred.reject(data);
  };
  const sendLoginLinkDeferred = new Deferred<Action>();
  dispatch({ type: actionTypes.SEND_LOGIN_LINK, status: apiStatuses.LOADING });

  productInfoDeferred.promise.then(productInfo => {
    fetchWithUuid(
      `/bestilling/c/${
        productInfo.owningClient
      }/api/v2/ff/sendLoginLink.json?email=${encodeURIComponent(
        email
      )}&redirectUri=${encodeURIComponent(redirectUri)}`
    )
      .then(checkError)
      .then(data => {
        dispatch({
          data,
          status: apiStatuses.SUCCESS,
          type: actionTypes.SEND_LOGIN_LINK
        });
        return sendLoginLinkDeferred.resolve(data);
      })
      .catch(failedCallback);
  }, failedCallback);

  return sendLoginLinkDeferred.promise;
};

export interface ICompanyPays {
  organizationNumber: string;
  companyName: string;
  reference: string;
  invoiceStreet: string;
  invoiceCity: string;
  invoiceZipCode: string;
}

export type createOrder = (
  props: {
    companyContactEmail?: string;
    companyContactFirstName?: string;
    companyContactLastName?: string;
    companyContactPhone?: string;
    spidId: number | string;
    sig: string;
    productInfo: IProductInfo;
    paymentMethodId: string;
    phoneNumber: string;
    address: IAddress;
    referer: string;
    redirectUri: string;
    purchasedSubscriptionId: number;
    paymentType: string;
    companyPays?: ICompanyPays;
    loyaltyCode?: string;
    loyaltyPartner?: string;
  }
) => (dispatch: Dispatch<Action>) => Promise<Action>;
export const createOrder: createOrder = ({
  companyContactEmail,
  companyContactFirstName,
  companyContactLastName,
  companyContactPhone,
  spidId,
  sig,
  productInfo,
  paymentMethodId,
  phoneNumber,
  address,
  referer,
  redirectUri,
  purchasedSubscriptionId,
  paymentType,
  companyPays,
  loyaltyCode,
  loyaltyPartner
}) => dispatch => {
  const failedCallback = (error: any) => {
    const errorData = error.data;
    let errorJson;
    try {
      errorJson = (errorData && JSON.parse(errorData)) || {};
    } catch (e) {
      errorJson = {};
    }
    const data = Object.assign({}, error, {
      ...errorJson,
      errorCode: errorJson.errorCode || 0,
      header: errorJson.summary || '',
      message: errorJson.message
    });
    if (data.status === 409) {
      dispatch({
        data,
        status: apiStatuses.CONFLICT,
        type: actionTypes.CREATE_ORDER
      });
    } else if (
      data.status === 422 &&
      JSON.stringify(data).indexOf(validationErrors.PURCHASE_FAILED) !== -1
    ) {
      dispatch({
        data,
        status: apiStatuses.VALIDATION_ERROR,
        type: actionTypes.CREATE_ORDER
      });
    } else {
      dispatch({
        data,
        status: apiStatuses.ERROR,
        type: actionTypes.CREATE_ORDER
      });
    }
    return createOrderDeferred.reject(data);
  };

  const createOrderDeferred = new Deferred<Action>();
  dispatch({ type: actionTypes.CREATE_ORDER, status: apiStatuses.LOADING });

  fetchWithUuid(
    `/bestilling/c/${
      productInfo.owningClient
    }/api/v2/ff/users/${spidId}/orders.json`,
    {
      body: JSON.stringify({
        apartmentNumber: address.apartmentNumber,
        cellphone: phoneNumber,
        city: address.city,
        ...(companyContactEmail && { companyContactEmail }),
        ...(companyContactFirstName && { companyContactFirstName }),
        ...(companyContactLastName && { companyContactLastName }),
        ...(companyContactPhone && { companyContactPhone }),
        companyName: (companyPays && companyPays.companyName) || '',
        diId: address.deliveryPointId,
        entrance: address.entrance,
        floor: address.floor,
        invoiceCity: (companyPays && companyPays.invoiceCity) || '',
        invoiceStreet: (companyPays && companyPays.invoiceStreet) || '',
        invoiceZipCode: (companyPays && companyPays.invoiceZipCode) || '',
        loyaltyPartner,
        loyaltyPartnerCardNumber: loyaltyCode,
        offersAccepted: false,
        organizationNumber:
          (companyPays && companyPays.organizationNumber) || '',
        paymentMethodId,
        paymentType,
        productId: productInfo.productId,
        productMappingId: productInfo.productMappingId,
        purchasedSubscriptionId,
        redirectUri,
        reference: (companyPays && companyPays.reference) || '',
        referer,
        street: address.streetAddress,
        streetNumber: address.houseNumber,
        subscriptionConfigurationId: productInfo.subscriptionConfigurationId,
        subscriptionTypeId: productInfo.subscriptionTypeId,
        zipCode: address.zipCode
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-score-spid-sig': sig
      },
      method: 'POST'
    }
  )
    .then(checkError)
    .then(res => res.json())
    .then(data => {
      dispatch({
        data,
        status: apiStatuses.SUCCESS,
        type: actionTypes.CREATE_ORDER
      });
      return createOrderDeferred.resolve(data);
    })
    .catch(failedCallback);

  return createOrderDeferred.promise;
};

export type FetchUserInfo = (
  spidId: string,
  sig: string,
  serverTime?: number,
  expiresIn?: number
) => (dispatch: Dispatch<Action>) => Promise<any>;
export type FetchUserInfoBound = (
  spidId: string,
  sig: string,
  serverTime?: number,
  expiresIn?: number
) => Promise<any>;
export const fetchUserInfo: FetchUserInfo = (
  spidId,
  sig,
  serverTime,
  expiresIn
) => (dispatch: Dispatch<Action>) => {
  const failedCallback = (data: any) => {
    if (
      data.status === 403 &&
      JSON.stringify(data).indexOf(
        validationErrors.USER_AGREEMENT_NOT_ACCEPTED
      ) !== -1
    ) {
      data.acceptedAgreement = false;
      dispatch({
        data,
        status: apiStatuses.SUCCESS,
        type: actionTypes.FETCH_USER_INFO
      });
    } else {
      dispatch({
        data,
        status: apiStatuses.ERROR,
        type: actionTypes.FETCH_USER_INFO
      });
    }
    return fetchUserInfoDeferred.reject(data);
  };

  dispatch({ type: actionTypes.FETCH_USER_INFO, status: apiStatuses.LOADING });

  if (!spidId || !sig || spidId === 'null') {
    dispatch({
      status: apiStatuses.VALIDATION_ERROR,
      type: actionTypes.FETCH_USER_INFO
    });
    fetchUserInfoDeferred.reject('No spidId or sig given.');
    return fetchUserInfoDeferred.promise;
  }

  const now = new Date();
  const nowAsEpoch = (now.getTime() - now.getMilliseconds()) / 1000;
  if (serverTime && expiresIn && nowAsEpoch > serverTime + expiresIn) {
    dispatch({
      status: apiStatuses.VALIDATION_ERROR,
      type: actionTypes.FETCH_USER_INFO
    });
    fetchUserInfoDeferred.reject('Token expired');
    return fetchUserInfoDeferred.promise;
  }

  productInfoDeferred.promise
    .then(productInfo => {
      return fetchWithUuid(
        `/bestilling/c/${
          productInfo.owningClient
        }/api/v2/ff/users/${spidId}.json`,
        {
          headers: {
            'x-score-spid-sig': sig
          }
        }
      );
    }, failedCallback)
    .then(checkError)
    .then(res => res.json())
    .then(data => {
      dispatch({
        data,
        status: apiStatuses.SUCCESS,
        type: actionTypes.FETCH_USER_INFO
      });
      userLoggedInDeferred.resolve({ spidId, sig });
      return fetchUserInfoDeferred.resolve(data);
    })
    .catch(failedCallback);
  return fetchUserInfoDeferred.promise;
};

export type createSpidUser = (
  email: string,
  firstName: string,
  lastName: string,
  phoneNumber: string,
  returnUrl: string,
  birthDate: string,
  address: IAddress
) => (dispatch: Dispatch<Action>) => Promise<any>;
export const createSpidUser: createSpidUser = (
  email,
  firstName,
  lastName,
  phoneNumber,
  returnUrl,
  birthDate,
  address: IAddress
) => dispatch => {
  const failedCallback = (data: any) => {
    dispatch({
      data,
      status: apiStatuses.ERROR,
      type: actionTypes.CREATE_SPID_USER
    });
    return deferred.reject(data);
  };

  dispatch({ type: actionTypes.CREATE_SPID_USER, status: apiStatuses.LOADING });
  const deferred = new Deferred();
  productInfoDeferred.promise
    .then(productInfo => {
      const {
        streetAddress,
        houseNumber,
        entrance,
        apartmentNumber,
        floor,
        zipCode,
        city,
        deliveryPointId
      } = address;
      const addressParams = `street=${encodeURIComponent(
        streetAddress
      )}&streetNumber=${encodeURIComponent(
        houseNumber
      )}&entrance=${encodeURIComponent(
        entrance
      )}&apartmentNumber=${encodeURIComponent(
        apartmentNumber
      )}&floor=${encodeURIComponent(floor)}&zipCode=${encodeURIComponent(
        zipCode
      )}&city=${encodeURIComponent(city)}&diId=${encodeURIComponent(
        deliveryPointId
      )}`;
      return fetchWithUuid(
        `/bestilling/c/${
          productInfo.owningClient
        }/api/v2/ff/createSpidUser.json?email=${encodeURIComponent(
          email
        )}&phoneNumber=${encodeURIComponent(
          phoneNumber
        )}&firstName=${encodeURIComponent(
          firstName
        )}&lastName=${encodeURIComponent(
          lastName
        )}&redirectUrl=${encodeURIComponent(
          returnUrl
        )}&dob=${encodeURIComponent(birthDate)}&${addressParams}`,
        {
          method: 'GET'
        }
      );
    }, failedCallback)
    .then(checkError)
    .then(res => res.json())
    .then(data => {
      dispatch({
        data,
        status: apiStatuses.SUCCESS,
        type: actionTypes.CREATE_SPID_USER
      });
      return deferred.resolve(data);
    })
    .catch(failedCallback);
  return deferred.promise;
};

export type checkDuplicateAndQuarantine = (
  productId: string,
  owningClient: string,
  spidId: string,
  sig: string
) => (dispatch: Dispatch<Action>) => Promise<any>;
export const checkDuplicateAndQuarantine: checkDuplicateAndQuarantine = (
  productId,
  owningClient,
  spidId,
  sig
) => dispatch => {
  const failedCallback = (data: any) => {
    dispatch({
      data,
      status: apiStatuses.ERROR,
      type: actionTypes.CHECK_DUPLICATE_AND_QUARANTINE
    });
    return checkDuplicateAndQuarantineDeferred.reject(data);
  };
  dispatch({
    status: apiStatuses.LOADING,
    type: actionTypes.CHECK_DUPLICATE_AND_QUARANTINE
  });

  fetchWithUuid(
    `/bestilling/c/${owningClient}/api/v2/ff/users/${spidId}/${productId}/isDuplicate.json`,
    {
      headers: {
        'x-score-spid-sig': sig
      }
    }
  )
    .then(checkError)
    .then(res => res.json())
    .then(data => {
      dispatch({
        data,
        status: apiStatuses.SUCCESS,
        type: actionTypes.CHECK_DUPLICATE_AND_QUARANTINE
      });
      return checkDuplicateAndQuarantineDeferred.resolve(data);
    })
    .catch(failedCallback);

  return checkDuplicateAndQuarantineDeferred.promise;
};

export type AddFamilySharing = (
  spidId: number,
  email: string,
  sig: string
) => (dispatch: Dispatch<Action>) => Promise<any>;
export const addFamilySharing: AddFamilySharing = (
  spidId: number,
  email: string,
  sig: string
) => (dispatch: Dispatch<Action>) => {
  const addFamilySharingDeferred = new Deferred();
  const failedCallback = (data: any) => {
    dispatch({
      data,
      status: apiStatuses.ERROR,
      type: actionTypes.ADD_FAMILY_SHARING
    });
    return addFamilySharingDeferred.reject(data);
  };

  dispatch({
    status: apiStatuses.LOADING,
    type: actionTypes.ADD_FAMILY_SHARING
  });

  productInfoDeferred.promise.then(productInfo => {
    fetchWithUuid(
      `/bestilling/c/${
        productInfo.owningClient
      }/api/v2/ff/users/${spidId}/addFamilySharing.json?email=${encodeURIComponent(
        email
      )}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-score-spid-sig': sig
        },
        method: 'POST'
      }
    )
      .then(checkError)
      .then(res => res.json())
      .then(data => {
        dispatch({
          data,
          status: apiStatuses.SUCCESS,
          type: actionTypes.ADD_FAMILY_SHARING
        });
        return addFamilySharingDeferred.resolve(data);
      })
      .catch(failedCallback);
  }, failedCallback);

  return addFamilySharingDeferred.promise;
};

export type SetSpidIdAndSigFromUrl = (spidId: string, sig: string) => Action;
export const setSpidIdAndSigFromUrl = (spidId: string, sig: string) => ({
  sig,
  spidId,
  type: actionTypes.SET_SIG_AND_SPIDID_FROM_URL
});

export type userEntersEmail = (email: string) => Action;
export const userEntersEmail: userEntersEmail = email => ({
  email,
  type: actionTypes.USER_ENTERS_EMAIL
});

export type UserEntersPhoneNumber = (num: string) => Action;
export const userEntersPhoneNumber: UserEntersPhoneNumber = num => ({
  number: num.replace(/\s/g, ''),
  type: actionTypes.USER_ENTERS_PHONE_NUMBER
});

export type UserEntersFirstName = (firstName: string) => Action;
export const userEntersFirstName: UserEntersFirstName = firstName => ({
  firstName,
  type: actionTypes.USER_ENTERS_FIRST_NAME
});

export type UserEntersLastName = (lastName: string) => Action;
export const userEntersLastName: UserEntersLastName = lastName => ({
  lastName,
  type: actionTypes.USER_ENTERS_LAST_NAME
});

export type UserEntersBirthDay = (birthDay: string) => Action;
export const userEntersBirthDay: UserEntersBirthDay = birthDay => ({
  birthDay,
  type: actionTypes.USER_ENTERS_BIRTH_DAY
});

export type UserEntersBirthMonth = (birthMonth: string) => Action;
export const userEntersBirthMonth: UserEntersBirthMonth = birthMonth => ({
  birthMonth,
  type: actionTypes.USER_ENTERS_BIRTH_MONTH
});

export type UserEntersBirthYear = (birthYear: string) => Action;
export const userEntersBirthYear: UserEntersBirthYear = birthYear => ({
  birthYear,
  type: actionTypes.USER_ENTERS_BIRTH_YEAR
});

export type UserEntersStreetAddress = (streetAddress: string) => Action;
export const userEntersStreetAddress: UserEntersStreetAddress = (
  streetAddress: string
) => ({
  streetAddress,
  type: actionTypes.USER_ENTERS_STREET_ADDRESS
});

export type UserEntersHouseNumber = (houseNumber: string) => Action;
export const userEntersHouseNumber: UserEntersHouseNumber = (
  houseNumber: string
) => ({
  houseNumber,
  type: actionTypes.USER_ENTERS_HOUSE_NUMBER
});

export type UserEntersEntrance = (entrance: string) => Action;
export const userEntersEntrance: UserEntersEntrance = (entrance: string) => ({
  entrance,
  type: actionTypes.USER_ENTERS_ENTRANCE
});

export type UserEntersFloor = (floor: string) => Action;
export const userEntersFloor: UserEntersFloor = (floor: string) => ({
  floor,
  type: actionTypes.USER_ENTERS_FLOOR
});

export type UserEntersApartmentNumber = (apartmentNumber: string) => Action;
export const userEntersApartmentNumber: UserEntersApartmentNumber = (
  apartmentNumber: string
) => ({
  apartmentNumber,
  type: actionTypes.USER_ENTERS_APARTMENT_NUMBER
});

export type UserEntersZipCode = (zipCode: string) => Action;
export const userEntersZipCode: UserEntersZipCode = zipCode => ({
  type: actionTypes.USER_ENTERS_ZIPCODE,
  zipCode
});

export type UserEntersCity = (city: string) => Action;
export const userEntersCity: UserEntersCity = (city: string) => ({
  city,
  type: actionTypes.USER_ENTERS_CITY
});

export type UserEntersDeliveryPointId = (deliveryPointId: string) => Action;
export const userEntersDeliveryPointId: UserEntersDeliveryPointId = (
  deliveryPointId: string
) => ({
  deliveryPointId,
  type: actionTypes.USER_ENTERS_DELIVERY_POINT_ID
});

export type UserEntersCode = (code: string) => Action;
export const userEntersCode: UserEntersCode = (code: string) => ({
  code,
  type: actionTypes.USER_ENTERS_CODE
});

export type ClearSpidToken = () => Action;
export const clearSpidToken = () => ({ type: actionTypes.CLEAR_SPID_TOKEN });

export type ShowTimeout = () => Action;
export const showTimeout = () => ({ type: actionTypes.SHOW_TIMEOUT });

export type HideTimeout = () => Action;
export const hideTimeout: HideTimeout = () => ({
  type: actionTypes.HIDE_TIMEOUT
});

export type SetReferer = (referer: string) => Action;
export const setReferer: SetReferer = (referer: string) => ({
  referer,
  type: actionTypes.SET_REFERER
});

export type SetRedirectUri = (redirectUri: string) => Action;
export const setRedirectUri: SetRedirectUri = (redirectUri: string) => ({
  redirectUri,
  type: actionTypes.SET_REDIRECT_URI
});

export type SetUtmSource = (source: string) => Action;
export const setUtmSource: SetUtmSource = (source: string) => ({
  source,
  type: actionTypes.SET_UTM_SOURCE
});

export type SetUtmMedium = (medium: string) => Action;
export const setUtmMedium: SetUtmMedium = (medium: string) => ({
  medium,
  type: actionTypes.SET_UTM_MEDIUM
});

export type SetUtmCampaign = (campaign: string) => Action;
export const setUtmCampaign: SetUtmCampaign = (campaign: string) => ({
  campaign,
  type: actionTypes.SET_UTM_CAMPAIGN
});

export type SetUtmTerm = (term: string) => Action;
export const setUtmTerm: SetUtmTerm = (term: string) => ({
  term,
  type: actionTypes.SET_UTM_TERM
});

export type SetUtmContent = (content: string) => Action;
export const setUtmContent: SetUtmContent = (content: string) => ({
  content,
  type: actionTypes.SET_UTM_CONTENT
});

export type UserSetOrgnumber = (orgnumber: string) => Action;
export const UserSetOrgnumber: UserSetOrgnumber = (orgnumber: string) => ({
  orgnumber,
  type: actionTypes.SET_BRREG_INPUT
});

export type UserEntersLoyaltyCode = (loyaltyCode: string) => Action;
export const userEntersLoyaltyCode: UserEntersLoyaltyCode = loyaltyCode => ({
  loyaltyCode,
  type: actionTypes.USER_ENTERS_LOYALTY_CODE
});

export const validateLoyaltyCode: ValidateLoyaltyCodeDeferred = (
  lp: string,
  code: string,
  ai: string
) => {
  const queryUrl: string =
    'lookup/vLC.htm?lp=' +
    lp.toUpperCase() +
    '&i=' +
    code +
    '&ai=' +
    (ai !== undefined ? ai : '');
  return fetchApi(`/bestilling/${queryUrl}`, actionTypes.VALIDATE_LOYALTY_CODE);
};



// WEBPACK FOOTER //
// ./src/actions/Actions.ts